import { app } from './firebase';
import { SITE } from '../config/site';

// The Functions SDK is fetched the first time something calls the backend, not on page load.
let fnPromise;
const loadFunctions = () => {
  if (!fnPromise) {
    fnPromise = import('firebase/functions').then(({ getFunctions, httpsCallable }) => ({
      functions: getFunctions(app, SITE.regions.functions), httpsCallable,
    }));
  }
  return fnPromise;
};

// One way to call the backend. Every error comes back as a plain sentence
// the interface can show to people.
const FRIENDLY = {
  'functions/unauthenticated': 'Please sign in and try again.',
  'functions/permission-denied': 'You don’t have permission to do that.',
  'functions/resource-exhausted': 'Too many attempts. Please wait a few minutes and try again.',
  'functions/unavailable': 'The service is temporarily unavailable. Please try again shortly.',
  'functions/deadline-exceeded': 'This is taking longer than expected. Please try again.',
  'functions/internal': 'Something went wrong on our side. Please try again.',
};

export class ApiError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
  }
}

export async function callFunction(name, data = {}, { timeout = 70000 } = {}) {
  try {
    const { functions, httpsCallable } = await loadFunctions();
    const fn = httpsCallable(functions, name, { timeout });
    const res = await fn(data);
    const payload = res?.data ?? {};
    if (payload && (payload.status === 'error' || payload.success === false)) {
      throw new ApiError(payload.message || 'Something went wrong. Please try again.', payload.code || 'error');
    }
    return payload;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    const code = err?.code || 'unknown';
    // Messages from our own functions are written for people; prefer them.
    const own = err?.message && !/^(internal|INTERNAL)$/.test(err.message) ? err.message : null;
    throw new ApiError(own || FRIENDLY[code] || 'Something went wrong. Please try again.', code);
  }
}
